import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { CardItem } from '@/components/CardItem'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { AppIcons } from '@/components/AppIcons'
import { useFetchMyStocks } from '@/domain/shares'
import { useTheme } from '@/hooks/useTheme'

interface StocksListProps {
  isLoading?: boolean
}

export const StocksList: React.FC<StocksListProps> = ({
  isLoading: isParentLoading,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()

  const { data: stocksData, isLoading: isStocksLoading } = useFetchMyStocks({
    page_size: 50,
  })

  const stocks = stocksData?.pages ?? []
  const isLoading = isParentLoading || isStocksLoading

  return (
    <BaseView gap={12}>
      {/* Section header — matching mobile: body.medium_16, content.quaternary */}
      <BaseView
        row
        align="center"
        gap={4}
        onClick={() => navigate('/stocks')}
        style={{ cursor: 'pointer' }}
      >
        <BaseText
          fontSize={16}
          fontFamily="medium"
          color="label.default"
        >
          {t('screens:stocks.title', { defaultValue: 'Stocks' })}
        </BaseText>
        <AppIcons name="chevronRight" size={22} />
      </BaseView>

      {isLoading ? (
        <BaseView center py={20}>
          <LoadingSpinner />
        </BaseView>
      ) : stocks.length === 0 ? (
        /* Empty state — matching mobile EmptyStocks card */
        <BaseView
          rounded={20}
          py={16}
          px={14}
          gap={22}
          onClick={() => navigate('/stocks')}
          style={{
            cursor: 'pointer',
            border: '1px solid rgba(255,255,255,0.08)',
            backgroundColor: theme.colors.background.cardItemRoot,
          }}
        >
          <BaseView row align="center" justify="space-between" fullWidth>
            <BaseText fontSize={14} fontFamily="medium" color="label.default">
              {t('banners:emptyStocks.title', { defaultValue: 'Explore Stocks' })}
            </BaseText>
            <AppIcons name="chevronRight" size={24} />
          </BaseView>
          <BaseText fontSize={14} fontFamily="regular" color="label.description">
            {t('banners:emptyStocks.description', {
              defaultValue: 'Invest in US stocks with as little as $1',
            })}
          </BaseText>
        </BaseView>
      ) : (
        /* Stock list — matching mobile MyStockItem with showDailyChange */
        <BaseView gap={8}>
          {stocks.map((stock) => {
            const dailyChange = Number(stock.daily_change_price ?? 0)
            const isLoss = dailyChange < 0
            const changeColor = isLoss ? '#F32C3E' : '#00CF46'

            return (
              <CardItem.Root
                key={stock.id}
                onPress={() => navigate(`/stocks/details/${stock.symbol}`)}
              >
                <CardItem.Avatar
                  uri={stock.logo_url}
                  name={stock.display_name}
                  size={44}
                  imageStyle={{ objectFit: 'contain', padding: '6px' }}
                />
                <CardItem.Content>
                  {/* Mobile order: name first, symbol second */}
                  <CardItem.Title
                    title={stock.display_name || stock.name}
                    numberOfLines={1}
                    style={{ maxWidth: '90%' }}
                  />
                  <CardItem.Description description={stock.symbol} />
                </CardItem.Content>
                <CardItem.Footer>
                  <BaseText
                    fontSize={16}
                    fontFamily="medium"
                    color="label.default"
                  >
                    {stock.formatted_holding_value}
                  </BaseText>
                  <BaseText
                    fontSize={14}
                    fontFamily="regular"
                    style={{
                      color: !stock.daily_change_price
                        ? undefined
                        : changeColor,
                    }}
                    color={
                      !stock.daily_change_price
                        ? 'label.description'
                        : undefined
                    }
                  >
                    {!stock.daily_change_price
                      ? '--'
                      : stock.formatted_daily_change_price}
                  </BaseText>
                </CardItem.Footer>
              </CardItem.Root>
            )
          })}
        </BaseView>
      )}
    </BaseView>
  )
}
